import * as THREE from "three";

interface Slot { readonly skeleton: THREE.Skeleton; readonly skins: readonly THREE.SkinnedMesh[]; start: number; lastUsed: number; pass: number }

/**
 * One bone texture for every rigid-rig instance (rigidRig.ts) instead of one per skeleton.
 *
 * three.js gives each `Skeleton` its own float `DataTexture` and re-uploads it every frame the skeleton is drawn. A
 * battle drew ~60 of them — every unit, tower, projectile and prop tree — so the phone paid ~60 separate texture
 * uploads a frame, each a driver round trip (and on Safari's ANGLE→Metal path each upload to a texture the previous
 * frame's GPU work still reads can force a copy or a wait). Here all of them write into slices of one shared array
 * and one texture, so a frame uploads bones once, however many units are alive, and a spawn creates no texture.
 *
 * Each skeleton's first texel rides to the shader in `bindMatrix[0][3]` — the w-row of an affine matrix, always 0 —
 * and `installShader` offsets the bone fetch by it and clears it before the matrix is used. Skeletons outside the
 * atlas (templates, any real skin) keep their own texture with offset 0, so the stock behaviour is unchanged for them.
 *
 * A slot belongs to a skeleton only while it is being drawn: the renderer calls `Skeleton.update` before any draw of a
 * frame, which (re)claims a slot, and slots idle for `IdleMs` go back to the free list. Pooled views, a torn-down
 * level or a culled prop hold nothing, and the atlas never keeps a discarded scene alive.
 */
export class BoneAtlas {
  /**
   * Texels per row: 64 bones, so the fortress (56) fits a row. Fixed, so a slot's texel index survives the texture
   * growing taller. The whole texture uploads each frame, so it is kept only as tall as the scene needs.
   */
  static readonly Width = 256;
  /** Starting rows: 512 bones, a 30-unit battle with its towers. Doubled whenever a slot does not fit. */
  static readonly InitialRows = 8;
  /** A skeleton not drawn for this long gives its slot back (a pooled unit, a culled tree, a finished level). */
  static readonly IdleMs = 1500;
  /** How often the idle sweep runs; it walks the live slots, so not every frame. */
  static readonly SweepMs = 500;
  /** Texels per bone matrix (four RGBA float columns). */
  private static readonly TexelsPerBone = 4;
  private static readonly FloatsPerTexel = 4;

  private static data: Float32Array<ArrayBuffer> = new Float32Array(BoneAtlas.Width * BoneAtlas.InitialRows * BoneAtlas.FloatsPerTexel);
  private static texture = BoneAtlas.makeTexture(BoneAtlas.data, BoneAtlas.InitialRows);
  private static readonly slots = new Map<THREE.Skeleton, Slot>();
  /** Free texel ranges `[start, length]`, sorted by start and coalesced. */
  private static free: [number, number][] = [[0, BoneAtlas.Width * BoneAtlas.InitialRows]];
  private static lastSweep = 0;
  /** The atlas is already flagged for upload in this render pass (see `markDirty`). */
  private static flagged = false;
  /** Render passes seen so far (see `markDirty`). */
  private static pass = 0;
  private static shaderInstalled = false;

  /**
   * Puts a cloned rig's skeleton on the atlas. `skins` are the meshes bound to it: their `bindMatrix` carries the
   * slot's offset to the shader.
   */
  static adopt(skeleton: THREE.Skeleton, skins: readonly THREE.SkinnedMesh[]): void {
    BoneAtlas.members.set(skeleton, skins as THREE.SkinnedMesh[]);
    const stock = THREE.Skeleton.prototype.update;
    skeleton.update = function (this: THREE.Skeleton): void {
      const slot = BoneAtlas.claim(this, skins);
      // The stock update flags the texture for upload on every call; the atlas decides that once per frame instead.
      const texture = this.boneTexture;
      this.boneTexture = null;
      stock.call(this);
      this.boneTexture = texture;
      BoneAtlas.markDirty(slot);
    };
  }

  /** The meshes bound to each adopted skeleton (the same array a slot carries, so `join` reaches a live slot too). */
  private static readonly members = new WeakMap<THREE.Skeleton, THREE.SkinnedMesh[]>();

  /**
   * Binds one more mesh to an adopted skeleton (a skin kit's attachments, render/skins/kit/runtime.ts): it gets the
   * slot's offset now and whenever the slot moves. A skeleton not on the atlas needs nothing (offset 0).
   */
  static join(skeleton: THREE.Skeleton, mesh: THREE.SkinnedMesh): void {
    const list = BoneAtlas.members.get(skeleton);
    if (!list || list.includes(mesh)) return;
    list.push(mesh);
    const slot = BoneAtlas.slots.get(skeleton);
    mesh.bindMatrix.elements[3] = slot ? slot.start : 0;
  }

  /** Unbinds a mesh `join` added (the attachments come off when the skin changes). */
  static leave(skeleton: THREE.Skeleton, mesh: THREE.SkinnedMesh): void {
    const list = BoneAtlas.members.get(skeleton);
    const i = list ? list.indexOf(mesh) : -1;
    if (i >= 0) list!.splice(i, 1);
    mesh.bindMatrix.elements[3] = 0;
  }

  /** Returns a skeleton's slot now (a view being thrown away); it would otherwise lapse after `IdleMs`. */
  static release(skeleton: THREE.Skeleton): void {
    const slot = BoneAtlas.slots.get(skeleton);
    if (slot) BoneAtlas.drop(slot);
  }

  /** Texels in use and texture rows, for the perf overlay and tests. */
  static stats(): { liveSkeletons: number; rows: number } { return { liveSkeletons: BoneAtlas.slots.size, rows: BoneAtlas.texture.image.height }; }

  /**
   * Offsets the stock bone fetch by the skeleton's first texel (`bindMatrix[0][3]`) and uses the bind matrix with
   * that element cleared. Must run before any skinned program compiles.
   */
  static installShader(): void {
    if (BoneAtlas.shaderInstalled) return;
    BoneAtlas.shaderInstalled = true;
    THREE.ShaderChunk.skinning_pars_vertex = `#ifdef USE_SKINNING
	uniform mat4 bindMatrix;
	uniform mat4 bindMatrixInverse;
	uniform highp sampler2D boneTexture;
	mat4 atlasBindMatrix() { mat4 m = bindMatrix; m[0][3] = 0.0; return m; }
	mat4 getBoneMatrix( const in float i ) {
		int size = textureSize( boneTexture, 0 ).x;
		int j = int( bindMatrix[0][3] + 0.5 ) + int( i ) * 4;
		int x = j % size;
		int y = j / size;
		vec4 v1 = texelFetch( boneTexture, ivec2( x, y ), 0 );
		vec4 v2 = texelFetch( boneTexture, ivec2( x + 1, y ), 0 );
		vec4 v3 = texelFetch( boneTexture, ivec2( x + 2, y ), 0 );
		vec4 v4 = texelFetch( boneTexture, ivec2( x + 3, y ), 0 );
		return mat4( v1, v2, v3, v4 );
	}
#endif`;
    const bind = /\bbindMatrix\b/g;
    THREE.ShaderChunk.skinning_vertex = THREE.ShaderChunk.skinning_vertex.replace(bind, "atlasBindMatrix()");
    THREE.ShaderChunk.skinnormal_vertex = THREE.ShaderChunk.skinnormal_vertex.replace(bind, "atlasBindMatrix()");
  }

  /**
   * Flags the atlas for upload once per render pass over the scene. The renderer updates the skeletons it sees before
   * drawing anything, but shadow casters outside the view only when the shadow pass reaches them — after the first
   * upload — and re-flagging for each of those re-uploaded the whole atlas between shadow draws (58 uploads a frame).
   * Those off-screen shadows now use the previous frame's pose.
   *
   * A new pass starts when a skeleton already updated in this one updates again (three.js updates each skeleton once
   * per `render`), or when the task ends (the next animation frame). A slot claimed just now (`pass` -1) has nothing
   * on the GPU yet, so it always flags.
   */
  private static markDirty(slot: Slot): void {
    const fresh = slot.pass < 0;
    if (slot.pass === BoneAtlas.pass && !fresh) { BoneAtlas.pass++; BoneAtlas.flagged = false; }
    slot.pass = BoneAtlas.pass;
    if (BoneAtlas.flagged && !fresh) return;
    BoneAtlas.texture.needsUpdate = true;
    if (BoneAtlas.flagged) return;
    BoneAtlas.flagged = true;
    queueMicrotask(() => { BoneAtlas.flagged = false; BoneAtlas.pass++; });
  }

  /** Claims (or keeps) the skeleton's slot; a new one has `pass` -1 until `markDirty` sees it. */
  private static claim(skeleton: THREE.Skeleton, skins: readonly THREE.SkinnedMesh[]): Slot {
    const now = performance.now();
    if (now - BoneAtlas.lastSweep > BoneAtlas.SweepMs) BoneAtlas.sweep(now);
    let slot = BoneAtlas.slots.get(skeleton);
    if (!slot) {
      const texels = skeleton.bones.length * BoneAtlas.TexelsPerBone;
      slot = { skeleton, skins, start: BoneAtlas.allocate(texels), lastUsed: now, pass: -1 };
      BoneAtlas.slots.set(skeleton, slot);
      BoneAtlas.bind(slot);
    }
    slot.lastUsed = now;
    return slot;
  }

  /** Points a skeleton's matrices and texture at its slot, and tells its meshes where the slot starts. */
  private static bind(slot: Slot): void {
    const s = slot.skeleton, f = BoneAtlas.FloatsPerTexel;
    s.boneMatrices = BoneAtlas.data.subarray(slot.start * f, (slot.start + s.bones.length * BoneAtlas.TexelsPerBone) * f);
    s.boneTexture = BoneAtlas.texture;
    for (const skin of slot.skins) skin.bindMatrix.elements[3] = slot.start;
  }

  private static drop(slot: Slot): void {
    BoneAtlas.slots.delete(slot.skeleton);
    const s = slot.skeleton;
    // Detached: the next draw re-claims a slot. Its own small arrays keep a stray update from writing someone else's.
    s.boneMatrices = new Float32Array(s.bones.length * 16);
    s.boneTexture = null;
    for (const skin of slot.skins) skin.bindMatrix.elements[3] = 0;
    BoneAtlas.giveBack(slot.start, s.bones.length * BoneAtlas.TexelsPerBone);
  }

  private static sweep(now: number): void {
    BoneAtlas.lastSweep = now;
    for (const slot of [...BoneAtlas.slots.values()]) if (now - slot.lastUsed > BoneAtlas.IdleMs) BoneAtlas.drop(slot);
  }

  private static allocate(texels: number): number {
    for (;;) {
      for (let i = 0; i < BoneAtlas.free.length; i++) {
        const [start, len] = BoneAtlas.free[i];
        if (len < texels) continue;
        if (len === texels) BoneAtlas.free.splice(i, 1);
        else BoneAtlas.free[i] = [start + texels, len - texels];
        return start;
      }
      BoneAtlas.grow();
    }
  }

  private static giveBack(start: number, len: number): void {
    const free = BoneAtlas.free;
    let i = 0;
    while (i < free.length && free[i][0] < start) i++;
    free.splice(i, 0, [start, len]);
    // Coalesce with the neighbours so a large rig (the fortress) can reuse space that small ones freed.
    if (i + 1 < free.length && free[i][0] + free[i][1] === free[i + 1][0]) { free[i][1] += free[i + 1][1]; free.splice(i + 1, 1); }
    if (i > 0 && free[i - 1][0] + free[i - 1][1] === free[i][0]) { free[i - 1][1] += free[i][1]; free.splice(i, 1); }
  }

  /** Doubles the rows; the width is fixed, so every live slot keeps its texel index and only its views move. */
  private static grow(): void {
    const rows = BoneAtlas.texture.image.height;
    const data = new Float32Array(BoneAtlas.Width * rows * 2 * BoneAtlas.FloatsPerTexel);
    data.set(BoneAtlas.data);
    BoneAtlas.texture.dispose();
    BoneAtlas.data = data;
    BoneAtlas.texture = BoneAtlas.makeTexture(data, rows * 2);
    BoneAtlas.giveBack(BoneAtlas.Width * rows, BoneAtlas.Width * rows);
    for (const slot of BoneAtlas.slots.values()) BoneAtlas.bind(slot);
  }

  private static makeTexture(data: Float32Array<ArrayBuffer>, rows: number): THREE.DataTexture {
    const t = new THREE.DataTexture(data, BoneAtlas.Width, rows, THREE.RGBAFormat, THREE.FloatType);
    t.needsUpdate = true;
    return t;
  }
}
